'use client';

import { PublicPageLayout } from '@letsgo/PublicPageLayout';
import { Box, Container, Typography, TextField, Button, MenuItem, Alert, Link as MuiLink } from '@mui/material';
import Link from 'next/link';
import { useState } from 'react';
import { SUPPORT_EMAIL, REFUND_POLICY_USER_SUMMARY } from '@/lib/site-contact';

const SUBJECT_OPTIONS = [
  { value: 'general', label: 'General inquiry' },
  { value: 'sales', label: 'Sales / Pricing' },
  { value: 'billing-refund', label: 'Billing or refund request' },
  { value: 'support', label: 'Technical support' },
  { value: 'api-access', label: 'API access' },
  { value: 'press', label: 'Press / Media' },
];

const ContactPage = () => {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [subject, setSubject] = useState('');
  const [message, setMessage] = useState('');
  const [phone, setPhone] = useState('');
  const [status, setStatus] = useState<'idle' | 'submitting' | 'success' | 'error'>('idle');
  const [errorMessage, setErrorMessage] = useState('');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setErrorMessage('');
    if (!name.trim()) {
      setErrorMessage('Please enter your name.');
      return;
    }
    if (!email.trim()) {
      setErrorMessage('Please enter your email.');
      return;
    }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim())) {
      setErrorMessage('Please enter a valid email address.');
      return;
    }
    if (!subject) {
      setErrorMessage('Please select a subject.');
      return;
    }
    if (!message.trim()) {
      setErrorMessage('Please enter your message.');
      return;
    }
    setStatus('submitting');
    try {
      const res = await fetch('/api/contact', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: name.trim(),
          email: email.trim(),
          subject,
          message: message.trim(),
          ...(phone.trim() && { phone: phone.trim() }),
        }),
      });
      const data = await res.json().catch(() => ({}));
      if (res.ok) {
        setStatus('success');
        setName('');
        setEmail('');
        setSubject('');
        setMessage('');
        setPhone('');
      } else {
        setStatus('error');
        setErrorMessage(data.message || 'Something went wrong. Please try again.');
      }
    } catch {
      setStatus('error');
      setErrorMessage('Unable to send. Please try again or email us directly.');
    }
  };

  return (
    <PublicPageLayout>
      <Container maxWidth="md" sx={{ py: 8, px: { xs: 2, sm: 3 } }}>
        <Typography variant="h3" fontWeight={700} mb={1} color="text.primary" component="h1">
          Contact Us
        </Typography>
        <Typography variant="body1" color="text.secondary" mb={3}>
          Have questions or need support? Send us a message and we&apos;ll get back to you.
        </Typography>
        <Typography variant="body2" color="text.secondary" mb={2} component="p">
          <strong>Billing and refunds:</strong> there is no refund button inside the app. Choose <strong>Billing or refund request</strong> below
          (or email{' '}
          <MuiLink href={`mailto:${SUPPORT_EMAIL}`} color="primary">
            {SUPPORT_EMAIL}
          </MuiLink>
          ) and include the <strong>email on your FloAI account</strong> and what you&apos;d like refunded or canceled. We&apos;ll confirm and process requests that meet our policy (see{' '}
          <MuiLink component={Link} href="/faq" color="primary">
            FAQ
          </MuiLink>
          ).
        </Typography>
        <Typography variant="body2" color="text.secondary" mb={3} component="p">
          {REFUND_POLICY_USER_SUMMARY}
        </Typography>

        {status === 'success' && (
          <Alert severity="success" sx={{ mb: 3 }}>
            Thanks for reaching out. We typically respond within 1–2 business days.
          </Alert>
        )}

        {status === 'error' && errorMessage && (
          <Alert severity="error" sx={{ mb: 3 }} onClose={() => setStatus('idle')}>
            {errorMessage}
          </Alert>
        )}

        <Box
          component="form"
          onSubmit={handleSubmit}
          noValidate
          sx={{ display: 'flex', flexDirection: 'column', gap: 2, maxWidth: 560, mx: { xs: 0, md: 'auto' } }}
        >
          <TextField
            label="Name"
            required
            fullWidth
            value={name}
            onChange={(e) => setName(e.target.value)}
            error={status === 'error' && !name.trim()}
            autoComplete="name"
            aria-required="true"
          />
          <TextField
            label="Email"
            type="email"
            required
            fullWidth
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            error={status === 'error' && !email.trim()}
            autoComplete="email"
            aria-required="true"
          />
          <TextField
            select
            label="Subject"
            required
            fullWidth
            value={subject}
            onChange={(e) => setSubject(e.target.value)}
            error={status === 'error' && !subject}
            aria-required="true"
          >
            {SUBJECT_OPTIONS.map((opt) => (
              <MenuItem key={opt.value} value={opt.value}>
                {opt.label}
              </MenuItem>
            ))}
          </TextField>
          <TextField
            label="Phone (optional)"
            fullWidth
            value={phone}
            onChange={(e) => setPhone(e.target.value)}
            autoComplete="tel"
          />
          <TextField
            label="Message"
            required
            fullWidth
            multiline
            rows={4}
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            error={status === 'error' && !message.trim()}
            aria-required="true"
          />
          <Button
            type="submit"
            variant="contained"
            color="primary"
            size="large"
            disabled={status === 'submitting'}
            sx={{ mt: 1 }}
          >
            {status === 'submitting' ? 'Sending…' : 'Send message'}
          </Button>
        </Box>

        <Typography variant="body2" color="text.secondary" sx={{ mt: 3 }}>
          Or email us directly at{' '}
          <MuiLink href={`mailto:${SUPPORT_EMAIL}`} color="primary">
            {SUPPORT_EMAIL}
          </MuiLink>
        </Typography>

        <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
          We respond within 1–2 business days. For Enterprise or sales, see our{' '}
          <MuiLink component={Link} href="/pricing" color="primary">
            pricing
          </MuiLink>{' '}
          or contact sales.
        </Typography>
      </Container>
    </PublicPageLayout>
  );
};

export default ContactPage;
